import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useLang } from "../App";
import { membersI18n, propertiesI18n } from "../i18n";
import PageHeader from "../components/PageHeader";
import { PasswordInput } from "../components/PasswordInput";
import { setMemberSession, getMemberToken } from "../memberAuth";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls = "flen-input w-full";
const labelCls = "mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-ink/50";

export default function MemberAuth() {
  const { lang, t } = useLang();
  const m = membersI18n[lang];
  const p = propertiesI18n[lang];
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle");
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({
    first_name: "",
    last_name: "",
    company: "",
    email: "",
    phone: "",
    password: "",
    passwordConfirm: "",
    interests: [],
    message: "",
  });

  useEffect(() => {
    if (getMemberToken()) navigate("/espace/objets");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleInterest = (key) =>
    setRegForm((f) => ({
      ...f,
      interests: f.interests.includes(key) ? f.interests.filter((i) => i !== key) : [...f.interests, key],
    }));

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/members/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.detail === "pending") throw new Error("pending");
        if (data.detail === "rejected") throw new Error("rejected");
        throw new Error("invalid");
      }
      const data = await res.json();
      setMemberSession(data.token, data.member);
      navigate("/espace/objets");
    } catch (err) {
      setError(m[err.message] || m.invalid);
      setStatus("idle");
    }
  };

  const register = async (e) => {
    e.preventDefault();
    setError("");
    if (regForm.password !== regForm.passwordConfirm) {
      setError(m.mismatch);
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/members/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(regForm),
      });
      if (res.status === 409) throw new Error("exists");
      if (!res.ok) throw new Error("invalid");
      setStatus("done");
    } catch (err) {
      setError(m[err.message] || m.invalid);
      setStatus("idle");
    }
  };

  const forgotSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("sending");
    try {
      await fetch(`${API}/api/members/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
    } catch {
      /* always show done — no account enumeration */
    }
    setStatus("forgot-done");
  };

  return (
    <div data-testid="member-auth-page" className="min-h-screen bg-ivory text-ink">
      <PageHeader active="members" />
      <section className="mx-auto max-w-xl px-6 pb-28 pt-16 md:pt-24">
        <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-bronze">{m.navLabel}</p>
        <h1 className="mt-6 font-serif text-4xl font-medium leading-tight sm:text-5xl">
          {tab === "login" ? m.loginTitle : m.registerTitle}
        </h1>
        <p className="mt-4 text-sm font-light leading-relaxed text-ink/60">
          {tab === "login" ? m.loginSub : m.registerSub}
        </p>

        <div className="mt-10 flex gap-8 border-b border-ink/10" data-testid="member-auth-tabs">
          {[
            { key: "login", label: m.tabLogin },
            { key: "register", label: m.tabRegister },
          ].map((tb) => (
            <button
              key={tb.key}
              data-testid={`member-tab-${tb.key}`}
              onClick={() => {
                setTab(tb.key);
                setError("");
                setStatus("idle");
                setForgot(false);
              }}
              className={`pb-3 text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                tab === tb.key ? "border-b border-bronze text-ink" : "text-ink/40 hover:text-ink"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {status === "done" ? (
          <div data-testid="register-success" className="mt-12 border border-bronze/40 p-10">
            <span className="flex h-12 w-12 items-center justify-center border border-bronze text-bronze">
              <Check size={20} strokeWidth={1.5} />
            </span>
            <h2 className="mt-8 font-serif text-2xl font-medium">{m.registerDone}</h2>
            <p className="mt-3 text-sm font-light leading-relaxed text-ink/60">{m.registerDoneSub}</p>
          </div>
        ) : tab === "login" && forgot ? (
          <div className="mt-12">
            {status === "forgot-done" ? (
              <div data-testid="forgot-done">
                <p className="max-w-md text-sm font-light leading-relaxed text-ink/65">{m.forgotDone}</p>
                <button
                  data-testid="forgot-back-login"
                  onClick={() => {
                    setForgot(false);
                    setStatus("idle");
                  }}
                  className="mt-6 text-[11px] uppercase tracking-[0.2em] text-bronze transition-colors duration-300 hover:text-ink"
                >
                  ← {m.backToLogin}
                </button>
              </div>
            ) : (
              <form data-testid="member-forgot-form" onSubmit={forgotSubmit} className="space-y-8">
                <div>
                  <h2 className="font-serif text-2xl font-medium">{m.forgotTitle}</h2>
                  <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-ink/60">{m.forgotSub}</p>
                </div>
                <div>
                  <label htmlFor="f-email" className={labelCls}>{t.contact.email} *</label>
                  <input id="f-email" type="email" required data-testid="forgot-email-input" value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)} className={inputCls} />
                </div>
                <div className="flex flex-wrap items-center gap-6">
                  <button type="submit" data-testid="forgot-submit" disabled={status === "sending"}
                    className="group flex items-center gap-4 bg-bronze px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-ivory transition-colors duration-500 hover:bg-champagne hover:text-ink disabled:opacity-50">
                    {status === "sending" ? "…" : m.forgotBtn}
                    <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                  </button>
                  <button type="button" onClick={() => setForgot(false)}
                    className="text-[11px] uppercase tracking-[0.2em] text-ink/45 transition-colors duration-300 hover:text-ink">
                    {m.backToLogin}
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : tab === "login" ? (
          <form data-testid="member-login-form" onSubmit={login} className="mt-12 space-y-8">
            <div>
              <label htmlFor="m-email" className={labelCls}>{t.contact.email} *</label>
              <input id="m-email" type="email" required data-testid="member-email-input" value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label htmlFor="m-password" className={labelCls}>{m.password} *</label>
              <PasswordInput id="m-password" testid="member-password-input" value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className={inputCls} />
              <div className="mt-3 text-right">
                <button type="button" data-testid="forgot-link"
                  onClick={() => {
                    setForgot(true);
                    setForgotEmail(loginForm.email);
                    setError("");
                  }}
                  className="text-[11px] uppercase tracking-[0.2em] text-ink/45 transition-colors duration-300 hover:text-bronze">
                  {m.forgotLink}
                </button>
              </div>
            </div>
            {error && <p data-testid="member-login-error" className="text-sm text-red-800">{error}</p>}
            <button type="submit" data-testid="member-login-button" disabled={status === "sending"}
              className="group flex items-center gap-4 bg-bronze px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-ivory transition-colors duration-500 hover:bg-champagne hover:text-ink disabled:opacity-50">
              {status === "sending" ? "…" : m.loginBtn}
              <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </button>
          </form>
        ) : (
          <form data-testid="member-register-form" onSubmit={register} className="mt-12 space-y-8">
            <div className="grid gap-8 sm:grid-cols-2">
              <div>
                <label htmlFor="r-first" className={labelCls}>{t.contact.firstName} *</label>
                <input id="r-first" required data-testid="register-firstname" value={regForm.first_name}
                  onChange={(e) => setRegForm({ ...regForm, first_name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label htmlFor="r-last" className={labelCls}>{t.contact.lastName} *</label>
                <input id="r-last" required data-testid="register-lastname" value={regForm.last_name}
                  onChange={(e) => setRegForm({ ...regForm, last_name: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="r-company" className={labelCls}>
                {m.company} ({lang === "fr" ? "facultatif" : lang === "de" ? "freiwillig" : "optional"})
              </label>
              <input id="r-company" data-testid="register-company" value={regForm.company}
                onChange={(e) => setRegForm({ ...regForm, company: e.target.value })} className={inputCls} />
            </div>
            <div className="grid gap-8 sm:grid-cols-2">
              <div>
                <label htmlFor="r-email" className={labelCls}>{t.contact.email} *</label>
                <input id="r-email" type="email" required data-testid="register-email" value={regForm.email}
                  onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label htmlFor="r-phone" className={labelCls}>{t.contact.phone}</label>
                <input id="r-phone" type="tel" data-testid="register-phone" value={regForm.phone}
                  onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="r-password" className={labelCls}>{m.password} * (min. 8)</label>
              <PasswordInput id="r-password" minLength={8} testid="register-password" value={regForm.password}
                onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label htmlFor="r-password2" className={labelCls}>{m.confirmPassword} *</label>
              <PasswordInput id="r-password2" minLength={8} testid="register-password-confirm" value={regForm.passwordConfirm}
                onChange={(e) => setRegForm({ ...regForm, passwordConfirm: e.target.value })} className={inputCls} />
            </div>
            <div>
              <span className={labelCls}>{m.interests}</span>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="register-interests">
                {Object.entries(p.categories).map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    data-testid={`register-interest-${key}`}
                    onClick={() => toggleInterest(key)}
                    className={`border px-4 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                      regForm.interests.includes(key)
                        ? "border-bronze bg-bronze text-ivory"
                        : "border-ink/20 text-ink/60 hover:border-bronze hover:text-ink"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="r-message" className={labelCls}>{m.messageOpt}</label>
              <textarea id="r-message" rows={3} data-testid="register-message" value={regForm.message}
                onChange={(e) => setRegForm({ ...regForm, message: e.target.value })} className={`${inputCls} resize-none`} />
            </div>
            {error && <p data-testid="member-register-error" className="text-sm text-red-800">{error}</p>}
            <button type="submit" data-testid="member-register-button" disabled={status === "sending"}
              className="group flex items-center gap-4 bg-bronze px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-ivory transition-colors duration-500 hover:bg-champagne hover:text-ink disabled:opacity-50">
              {status === "sending" ? "…" : m.registerBtn}
              <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </button>
          </form>
        )}

        <Link to="/" data-testid="member-back-home"
          className="group mt-14 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.25em] text-ink/50 transition-colors duration-300 hover:text-bronze">
          <ArrowLeft size={14} className="transition-transform duration-500 group-hover:-translate-x-1" /> {m.backHome}
        </Link>
      </section>
    </div>
  );
}
